import { reactive, ref } from 'vue'
import { getAgendorAccess, getAgendorLinks, type AgendorAccess, type AgendorLink } from '@/services/wootdashAgendor'
import { onHubEvent } from '@/composables/useConversationRealtime'

// Agendor na tela: um estado só pra todos os ícones. O acesso é buscado uma vez por sessão do navegador; os vínculos
// dos contatos visíveis vão juntos num pedido só (os ícones pedem e o lote sai no próximo tique). Mudança chega pelo
// SignalR ("AgendorVinculoAlterado", só o id do contato) — nada de consultar em intervalo.

const access = ref<AgendorAccess | null>(null)
let accessInflight: Promise<void> | null = null
let currentToken = ''

/** contactId → vínculo; null = já perguntado e sem vínculo. Ausente = ainda não perguntado. */
const links = reactive(new Map<string, AgendorLink | null>())
const pending = new Set<string>()
let flushTimer: ReturnType<typeof setTimeout> | null = null
const BATCH = 200

export function loadAgendor(token: string, force = false): Promise<void> {
  if (!token) return Promise.resolve()
  currentToken = token
  if (access.value && !force) return Promise.resolve()
  if (accessInflight && !force) return accessInflight
  accessInflight = getAgendorAccess(token).then(a => { access.value = a }).finally(() => { accessInflight = null })
  return accessInflight
}

/** Depois de mudar a configuração ou o próprio acesso (Admin › Agendor). */
export function refreshAgendor(token: string): Promise<void> {
  links.clear()
  return loadAgendor(token, true)
}

async function flush() {
  flushTimer = null
  if (!access.value?.allowed || !currentToken) return
  const ids = [...pending]
  pending.clear()
  for (let i = 0; i < ids.length; i += BATCH) {
    const chunk = ids.slice(i, i + BATCH)
    const result = await getAgendorLinks(currentToken, chunk)
    if (!result.allowed) {
      if (access.value) access.value = { ...access.value, allowed: false }
      return
    }
    const byContact = new Map(result.links.map(l => [l.contactId, l]))
    chunk.forEach(id => links.set(id, byContact.get(id) ?? null))
  }
}

/** O ícone pede o vínculo do contato; `force` = rebuscar mesmo já conhecido. */
export function requestAgendorLink(contactId: string | null | undefined, force = false) {
  if (!contactId || (!force && links.has(contactId))) return
  pending.add(contactId)
  if (!flushTimer) flushTimer = setTimeout(() => { void flush() }, 30)
}

/** Resultado de uma ação (procurar, vincular, desvincular) já atualiza sem esperar o SignalR. */
export function setAgendorLink(contactId: string, link: AgendorLink | null) {
  links.set(contactId, link)
}

onHubEvent('AgendorVinculoAlterado', (evt: { contactId: string; linked: boolean }) => {
  if (!evt?.contactId) return
  if (!evt.linked) links.set(evt.contactId, null)
  else if (links.has(evt.contactId) || access.value?.allowed) requestAgendorLink(evt.contactId, true)
})

export function useAgendor() {
  return { access, links }
}

/** Só pra testes. */
export function resetAgendor() {
  access.value = null
  accessInflight = null
  currentToken = ''
  links.clear()
  pending.clear()
  if (flushTimer) clearTimeout(flushTimer)
  flushTimer = null
}
